import { useCallback, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import Tippy from '@tippyjs/react'
import Flag from 'react-flagkit'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import styles from './NotificationBar.module.css'

import Select from './Select'
import { useFiltersQuery } from '../services/filters'
import { getNotificationsAPI, updateNotificationsAPI } from '../lib/api/api'

const MARKETS = [
  { id: 'de', label: 'Deutschland', flag: 'DE' },
  { id: 'fr', label: 'Frankreich', flag: 'FR' },
  { id: 'it', label: 'Italien', flag: 'IT' },
  { id: 'es', label: 'Spanien', flag: 'ES' },
]

const normalizePresetId = (presetId) => {
  if (presetId === null || presetId === undefined) return null
  if (presetId === '' || presetId === 'None') return null
  return String(presetId)
}

export default function NotificationBar({ className, application = 'a2a' }) {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedPreset, setSelectedPreset] = useState(null)
  const [selectedMarkets, setSelectedMarkets] = useState(['de', 'fr', 'it', 'es'])
  const [notificationsEnabled, setNotificationsEnabled] = useState(true)

  const { data, isFetching } = useFiltersQuery(application)
  const queryClient = useQueryClient()

  const { data: notificationSettings } = useQuery({
    queryKey: ['notifications', application],
    queryFn: () => getNotificationsAPI(application),
    staleTime: 60000,
  })

  const { mutate: updateNotifications } = useMutation({
    mutationFn: updateNotificationsAPI,
    onSuccess: (_, variables) => {
      queryClient.setQueryData(['notifications', application], () => ({
        success: true,
        preset_id: variables.preset_id ?? null,
        notification: variables.notification ? 1 : 0,
        de: variables.markets.includes('de') ? 1 : 0,
        it: variables.markets.includes('it') ? 1 : 0,
        es: variables.markets.includes('es') ? 1 : 0,
        fr: variables.markets.includes('fr') ? 1 : 0,
      }))
    },
  })

  const presetOptions = useMemo(() => (
    Object.entries(data?.current_presets || {}).map(([preset_id, [preset_name]]) => {
      const normalizedId = normalizePresetId(preset_id)
      return {
        value: normalizedId,
        label: preset_name,
      }
    })
  ), [data?.current_presets])

  useEffect(() => {
    if (!selectedPreset) return
    if (!presetOptions.length) return
    if (!presetOptions.some(option => option.value === selectedPreset)) {
      setSelectedPreset(null)
    }
  }, [presetOptions, selectedPreset])

  const selectedPresetOption = useMemo(
    () => presetOptions.find(option => option.value === selectedPreset) || null,
    [presetOptions, selectedPreset]
  )

  useEffect(() => {
    if (!notificationSettings?.success) return

    const nextPreset = normalizePresetId(notificationSettings.preset_id)
    const nextNotification = Boolean(Number(notificationSettings.notification))
    const nextMarkets = MARKETS
      .filter((market) => Boolean(Number(notificationSettings[market.id])))
      .map((market) => market.id)

    setSelectedPreset((prev) => (prev === nextPreset ? prev : nextPreset))
    setNotificationsEnabled((prev) => (prev === nextNotification ? prev : nextNotification))
    setSelectedMarkets((prev) => {
      const areEqual = prev.length === nextMarkets.length && prev.every((value, index) => value === nextMarkets[index])
      return areEqual ? prev : nextMarkets
    })
  }, [notificationSettings])

  const triggerUpdate = useCallback((overrides = {}) => {
    const normalizedPreset = normalizePresetId(overrides.preset_id ?? selectedPreset)

    const payload = {
      application,
      preset_id: normalizedPreset,
      notification: overrides.notification ?? notificationsEnabled,
      markets: overrides.markets ?? selectedMarkets,
    }

    updateNotifications(payload)
  }, [application, selectedPreset, notificationsEnabled, selectedMarkets, updateNotifications])

  const ensureNotificationPrerequisites = useCallback(() => {
    if (!selectedPreset) {
      toast.info('Bitte wähle zuerst ein Preset aus.')
      return false
    }
    if (!selectedMarkets.length) {
      toast.info('Bitte mindestens einen Markt auswählen.')
      return false
    }
    return true
  }, [selectedPreset, selectedMarkets])

  const handleToggleNotifications = useCallback(() => {
    const nextValue = !notificationsEnabled
    if (nextValue && !ensureNotificationPrerequisites()) {
      return
    }
    setNotificationsEnabled(nextValue)
    triggerUpdate({ notification: nextValue })
  }, [notificationsEnabled, ensureNotificationPrerequisites, triggerUpdate])

  return (
    <Tippy
      visible={isOpen}
      onClickOutside={() => setIsOpen(false)}
      interactive
      placement='bottom-start'
      arrow={false}
      className={styles.tooltip}
      content={
        <div className={styles.dropdownContent}>
          <section>
            <header>Presets</header>
            <div className={styles.presetSelectWrapper}>
              <Select
                isLoading={isFetching}
                options={presetOptions}
                value={selectedPresetOption}
                placeholder='Preset auswählen'
                onChange={(option) => {
                  const nextPreset = option?.value || null
                  const normalized = normalizePresetId(nextPreset)
                  setSelectedPreset(normalized)
                  triggerUpdate({ preset_id: normalized })
                }}
                className={styles.presetSelect}
              />
            </div>
          </section>

          <section>
            <header>Märkte</header>
            <div className={styles.marketFlags}>
              {MARKETS.map((market) => {
                const isActive = selectedMarkets.includes(market.id)
                return (
                  <button
                    key={market.id}
                    type='button'
                    className={classNames(styles.flagButton, {
                      [styles.flagButtonActive]: isActive,
                    })}
                    onClick={() => {
                      setSelectedMarkets((prev) => {
                        const nextMarkets = prev.includes(market.id)
                          ? prev.filter((id) => id !== market.id)
                          : [...prev, market.id]
                        triggerUpdate({ markets: nextMarkets })
                        return nextMarkets
                      })
                    }}
                  >
                    <Flag country={market.flag} size={24} />
                  </button>
                )
              })}
            </div>
          </section>

          <section>
            <header>Discord Pings</header>
            <button
              type='button'
              onClick={handleToggleNotifications}
              className={classNames(styles.toggleButton, {
                [styles.toggleButtonActive]: notificationsEnabled,
              })}
            >
              <span className={styles.toggleIndicator} aria-hidden />
              <span>{notificationsEnabled ? 'Aktiviert' : 'Deaktiviert'}</span>
            </button>
          </section>
        </div>
      }
    >
      <button
        type='button'
        onClick={() => setIsOpen((prev) => !prev)}
        className={classNames(
          'relative inline-flex items-center gap-2 h-9 px-3 rounded-lg border text-sm font-medium transition-colors focus:outline-none',
          isOpen
            ? 'border-primary text-primary bg-secondary'
            : 'border-neutral-200 dark:border-neutral-700 text-textTitle bg-background hover:border-primary',
          className
        )}
      >
        <i className='bx bx-bell text-[17px] leading-none' aria-hidden />
        Pings
        <i className={classNames('bx bx-chevron-down text-base leading-none -ml-0.5', isOpen ? 'text-primary' : 'text-neutral-400')} aria-hidden />
      </button>
    </Tippy>
  )
}
